import React from 'react';
import { PiLeaf, PiPlant } from 'react-icons/pi';

import {
  SEED_TWO_PANEL_BACK_TO_CROPS_TOOLTIP,
  SEED_TWO_PANEL_CROP_LABEL,
  SEED_TWO_PANEL_SEED_STRIP_TOOLTIP,
  SEED_TWO_PANEL_VARIETY_LABEL,
  SEED_TWO_PANEL_VARIETY_STRIP_TOOLTIP,
} from 'constants/uiConstants';
import { GcVariety } from 'interfaces/IGlobalCatalogue';

import { getStepTwoPanelReopenTooltip, StepSelectorOption } from './stepConfig';
import StepTwoPanel from './StepTwoPanel';
import VarietyListPanel from './VarietyListPanel';

interface SeedVarietyTwoPanelProps {
  crops: StepSelectorOption[];
  highlightedCrop: StepSelectorOption | null;
  isSeedSelected: boolean;
  highlightedVariety: GcVariety | null;
  varietySearchText: string;
  decideLaterLabel: string;
  onCropClick: (crop: StepSelectorOption) => void;
  onVarietySelect: (variety: GcVariety) => void;
  onDecideLater: () => void;
  onBackToSeeds: () => void;
  onVarietySearchChange: (value: string) => void;
  cropEmptyAction?: React.ReactNode;
  varietyEmptyAction?: React.ReactNode;
}

// Seed group's own instance of the generic two-panel layout (StepTwoPanel):
// a crop list on the left, its varieties on the right. See StepTwoPanel for
// the shared collapse/expand mechanics this wraps.
const SeedVarietyTwoPanel: React.FC<SeedVarietyTwoPanelProps> = ({
  crops,
  highlightedCrop,
  isSeedSelected,
  highlightedVariety,
  varietySearchText,
  decideLaterLabel,
  onCropClick,
  onVarietySelect,
  onDecideLater,
  onBackToSeeds,
  onVarietySearchChange,
  cropEmptyAction,
  varietyEmptyAction,
}) => (
  <StepTwoPanel
    primaryOptions={crops}
    highlightedPrimary={highlightedCrop}
    isSecondaryOpen={isSeedSelected}
    onPrimaryClick={onCropClick}
    onBackToPrimaryList={onBackToSeeds}
    primaryEmptyAction={cropEmptyAction}
    primaryStripLabel={SEED_TWO_PANEL_CROP_LABEL}
    primaryStripIcon={
      <PiPlant size={14} className="flex-shrink-0 text-gray-dark" />
    }
    primaryStripTooltip={SEED_TWO_PANEL_SEED_STRIP_TOOLTIP}
    backTooltip={SEED_TWO_PANEL_BACK_TO_CROPS_TOOLTIP}
    secondaryContent={
      <VarietyListPanel
        className="overflow-hidden"
        varieties={highlightedCrop?.varieties ?? []}
        searchText={varietySearchText}
        highlightedVariety={highlightedVariety}
        decideLaterLabel={decideLaterLabel}
        onSearchChange={onVarietySearchChange}
        onSelectVariety={onVarietySelect}
        onDecideLater={onDecideLater}
        emptyAction={varietyEmptyAction}
      />
    }
    secondaryStripActiveLabel={
      highlightedVariety?.varietyName ?? SEED_TWO_PANEL_VARIETY_LABEL
    }
    secondaryStripActiveIcon={
      <PiLeaf size={14} className="flex-shrink-0 text-gray-dark" />
    }
    secondaryStripActiveTooltip={
      highlightedCrop
        ? getStepTwoPanelReopenTooltip(highlightedCrop.label, 'varieties')
        : ''
    }
    onReopenSecondary={onCropClick}
    secondaryStripInertLabel={SEED_TWO_PANEL_VARIETY_LABEL}
    secondaryStripInertIcon={
      <PiLeaf size={14} className="flex-shrink-0 text-gray-medium" />
    }
    secondaryStripInertTooltip={SEED_TWO_PANEL_VARIETY_STRIP_TOOLTIP}
  />
);

export default SeedVarietyTwoPanel;
